import { useRef, useEffect, useCallback } from 'react';
import { ForgePaginator } from '@tylertech/forge-react';
import '@tylertech/forge/paginator';

interface PaginatorChangeDetail {
  type: 'previous-page' | 'next-page' | 'first-page' | 'last-page' | 'page-size';
  pageSize: number;
  pageIndex: number;
  offset: number;
}

export interface PaginationProps {
  totalCount: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

export function Pagination({
  totalCount,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}: PaginationProps): JSX.Element | null {
  const paginatorRef = useRef<HTMLElement>(null);

  const handleChange = useCallback(
    (e: Event) => {
      const {
        type,
        pageIndex,
        pageSize: newPageSize,
      } = (e as CustomEvent<PaginatorChangeDetail>).detail;
      if (type === 'page-size') {
        onPageSizeChange(newPageSize);
      } else {
        onPageChange(pageIndex + 1);
      }
    },
    [onPageChange, onPageSizeChange]
  );

  useEffect(() => {
    const el = paginatorRef.current;
    if (!el) return;
    el.addEventListener('forge-paginator-change', handleChange);
    return () => el.removeEventListener('forge-paginator-change', handleChange);
  }, [handleChange]);

  if (totalCount === 0) return null;

  return (
    <ForgePaginator
      ref={paginatorRef as React.Ref<unknown>}
      total={totalCount}
      pageIndex={currentPage - 1}
      pageSize={pageSize}
      pageSizeOptions={pageSizeOptions}
    />
  );
}
